import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicSucculentList, SucculentPlant } from '../utils/flowerApi';
import { SucculentCard } from '../components/SucculentCard';
import styles from './PublicList.module.css';

/**
 * 公开多肉列表页 — 3列网格，分页加载，回到顶部
 */
export const PublicList: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<SucculentPlant[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const PAGE_SIZE = 12;

  const fetchList = useCallback(async (page: number, append = false) => {
    if (loading || (!append && !hasMore)) return;
    setLoading(true);
    try {
      const { items: newItems } = await getPublicSucculentList(page, PAGE_SIZE);
      setItems(prev => append ? [...prev, ...newItems] : newItems);
      setHasMore(newItems.length >= PAGE_SIZE);
      setPageNum(page);
    } catch (err) {
      console.error('Failed to fetch public list:', err);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore]);

  useEffect(() => {
    fetchList(1);
  }, []);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrollTop(y);
      setShowBackToTop(y > 500);

      if (y + window.innerHeight >= document.documentElement.scrollHeight - 200) {
        if (hasMore && !loading) {
          fetchList(pageNum + 1, true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageNum, hasMore, loading, fetchList]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/')}>
          &#8592;
        </button>
        <h1 className={styles.headerTitle}>公开的多肉</h1>
        <span className={styles.headerSpacer} />
      </header>

      {/* Grid */}
      <div className={styles.grid}>
        {items.map((plant) => (
          <SucculentCard
            key={plant.succulent_plant_id}
            plant={plant}
            variant="public"
            onClick={() => navigate(`/succulents/${plant.succulent_plant_id}`, { state: { plant, source: 'public' } })}
          />
        ))}
      </div>

      {/* Loading more */}
      {loading && items.length > 0 && (
        <div className={styles.loadingMore}>加载中...</div>
      )}

      {/* No more */}
      {!hasMore && items.length > 0 && (
        <p className={styles.noMore}>没有更多了</p>
      )}

      {/* Empty */}
      {!loading && items.length === 0 && (
        <p className={styles.empty}>暂无公开多肉</p>
      )}

      {/* Footer */}
      <footer className={styles.footer}>
        <span>ICP备案号:</span>
        <a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">
          闽ICP备2025102433号-1
        </a>
      </footer>

      {/* Back to top */}
      {showBackToTop && (
        <button className={styles.backToTop} onClick={scrollToTop}>
          &#8593;
        </button>
      )}
    </div>
  );
};
